import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Lock, Crown, BookOpen, Users, Trophy, Target, 
  Sparkles, ArrowRight, Shield, Zap, Star, Brain
} from 'lucide-react';

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i) => ({
    opacity: 1, y: 0,
    transition: { delay: i * 0.1, duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }
  })
};

const ecosystemFeatures = [
  {
    icon: Brain,
    title: 'Zihin Mimarisi Sistemi',
    desc: 'Kişiselleştirilmiş zihinsel gelişim yol haritası, günlük ritüeller ve bilinçaltı dönüşüm programı',
    tier: 'masterclass'
  },
  {
    icon: Target,
    title: 'Puan & Seviye Sistemi',
    desc: 'Her adımınız ödüllendirilir. Zihin Puanları kazanın, seviye atlayın, başarılarınızı takip edin',
    tier: 'masterclass'
  },
  {
    icon: Users,
    title: 'Topluluk & Network',
    desc: 'Aynı vizyonu paylaşan insanlarla güçlü bir network. VIP etkinlikler, mastermind grupları',
    tier: 'full'
  },
  {
    icon: BookOpen,
    title: 'Video Eğitim Kütüphanesi',
    desc: 'Tüm online ve video eğitimlere sınırsız erişim. Kendi hızınızda ilerleyin',
    tier: 'full'
  },
  {
    icon: Trophy,
    title: 'Sertifika Programı',
    desc: 'Tamamladığınız her eğitim için dijital sertifika. Kariyerinize değer katın',
    tier: 'full'
  },
  {
    icon: Zap,
    title: 'Akıllı Öneri Motoru',
    desc: 'Yapay zeka destekli kişisel gelişim önerileri, ruh halinize göre ritüel planlaması',
    tier: 'full'
  },
];

const journeySteps = [
  {
    num: '01',
    title: 'Masterclass',
    desc: 'Zihin mimarinizi keşfedin. Bilinçaltı dönüşüm yolculuğunuza başlayın.',
    active: true
  },
  {
    num: '02',
    title: 'Online / Video Eğitim',
    desc: 'Derinlemesine öğrenin. Kendi hızınızda, istediğiniz yerden ilerleyin.',
    active: false
  },
  {
    num: '03',
    title: 'Tam Erişim',
    desc: 'Ekosistemin tüm imkanlarından faydalanın. Topluluğun aktif bir parçası olun.',
    active: false
  },
];

export default function PanelGate() {
  return (
    <div className="min-h-screen bg-[#060208] relative overflow-hidden">
      {/* Ambient background */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-[-20%] left-[-10%] w-[60vw] h-[60vw] rounded-full bg-purple-900/8 blur-[120px]" />
        <div className="absolute bottom-[-20%] right-[-10%] w-[50vw] h-[50vw] rounded-full bg-indigo-900/6 blur-[100px]" />
      </div>

      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 py-12 sm:py-20">
        {/* Header with lock icon */}
        <motion.div
          className="text-center mb-12 sm:mb-16"
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          custom={0}
        >
          <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-gradient-to-br from-purple-500/20 to-indigo-500/20 border border-purple-500/20 mb-6" data-testid="panel-gate-lock-icon">
            <Lock className="w-8 h-8 sm:w-10 sm:h-10 text-purple-400" />
          </div>
          
          <h1 className="font-[Cormorant_Garamond] text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight">
            Panel Erişimi
          </h1>
          <p className="font-[Outfit] text-base sm:text-lg text-white/60 max-w-2xl mx-auto leading-relaxed">
            Panelimiz, eğitimlerimizi satın alan kişilere özel bir ekosistemdir.
            Bu alanın parçası olmak ve tüm imkanlardan faydalanmak için
            yolculuğunuza Masterclass ile başlayın.
          </p>
        </motion.div>

        {/* Journey Steps */}
        <motion.div
          className="mb-14 sm:mb-20"
          initial="hidden"
          animate="visible"
        >
          <motion.h2 
            className="font-[Outfit] text-sm uppercase tracking-[0.2em] text-purple-400/70 text-center mb-8"
            variants={fadeUp} custom={1}
          >
            Yolculuk Haritanız
          </motion.h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
            {journeySteps.map((step, i) => (
              <motion.div
                key={step.num}
                className={`relative p-6 rounded-2xl border transition-all ${
                  step.active
                    ? 'bg-gradient-to-br from-purple-500/10 to-indigo-500/10 border-purple-500/30'
                    : 'bg-white/[0.02] border-white/[0.06]'
                }`}
                variants={fadeUp}
                custom={i + 2}
                data-testid={`journey-step-${step.num}`}
              >
                {step.active && (
                  <div className="absolute -top-3 left-6">
                    <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-purple-500/20 border border-purple-500/30 text-purple-300 text-xs font-[Outfit] font-medium">
                      <Star className="w-3 h-3" /> Buradan Başlayın
                    </span>
                  </div>
                )}
                <div className={`text-3xl font-[Cormorant_Garamond] font-bold mb-3 ${
                  step.active ? 'text-purple-400' : 'text-white/15'
                }`}>
                  {step.num}
                </div>
                <h3 className={`font-[Outfit] text-lg font-semibold mb-2 ${
                  step.active ? 'text-white' : 'text-white/40'
                }`}>
                  {step.title}
                </h3>
                <p className={`font-[Outfit] text-sm leading-relaxed ${
                  step.active ? 'text-white/60' : 'text-white/25'
                }`}>
                  {step.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Ecosystem Features */}
        <motion.div
          className="mb-14 sm:mb-20"
          initial="hidden"
          animate="visible"
        >
          <motion.h2
            className="font-[Outfit] text-sm uppercase tracking-[0.2em] text-purple-400/70 text-center mb-8"
            variants={fadeUp} custom={5}
          >
            Sizi Neler Bekliyor
          </motion.h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {ecosystemFeatures.map((f, i) => {
              const Icon = f.icon;
              return (
                <motion.div
                  key={f.title}
                  className="group p-5 rounded-xl bg-white/[0.02] border border-white/[0.06] hover:border-purple-500/20 hover:bg-purple-500/[0.04] transition-all duration-300"
                  variants={fadeUp}
                  custom={i + 6}
                  data-testid={`ecosystem-feature-${i}`}
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center group-hover:bg-purple-500/20 transition-colors">
                      <Icon className="w-5 h-5 text-purple-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-[Outfit] text-sm font-semibold text-white/90">{f.title}</h3>
                        {f.tier === 'full' && (
                          <span className="flex-shrink-0 px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-500/15 text-amber-400 border border-amber-500/20">
                            Tam Erişim
                          </span>
                        )}
                      </div>
                      <p className="font-[Outfit] text-xs text-white/40 leading-relaxed">{f.desc}</p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        {/* CTA Section */}
        <motion.div
          className="text-center"
          initial="hidden"
          animate="visible"
        >
          <motion.div
            className="inline-block p-8 sm:p-10 rounded-3xl bg-gradient-to-br from-purple-500/8 to-indigo-500/8 border border-purple-500/15"
            variants={fadeUp}
            custom={12}
            data-testid="panel-gate-cta"
          >
            <Crown className="w-10 h-10 text-purple-400 mx-auto mb-4" />
            <h2 className="font-[Cormorant_Garamond] text-2xl sm:text-3xl font-bold text-white mb-3">
              Yolculuğunuz Masterclass ile Başlıyor
            </h2>
            <p className="font-[Outfit] text-sm text-white/50 max-w-md mx-auto mb-6 leading-relaxed">
              Panelin imkanlarından faydalanabilmek ve topluluğun parçası olabilmek
              için ilk adımınız Masterclass. Sonrasında online, video veya
              yüz yüze eğitimlerden birini alarak tüm ekosisteme dahil olabilirsiniz.
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/masterclass"
                className="group inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white font-[Outfit] font-medium text-sm transition-all duration-300 shadow-lg shadow-purple-500/20"
                data-testid="panel-gate-masterclass-btn"
              >
                <Sparkles className="w-4 h-4" />
                Masterclass'a Başvur
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl border border-white/10 hover:border-white/20 text-white/50 hover:text-white/70 font-[Outfit] text-sm transition-all"
                data-testid="panel-gate-home-btn"
              >
                Ana Sayfaya Dön
              </Link>
            </div>
          </motion.div>

          {/* Already purchased? */}
          <motion.p
            className="mt-6 font-[Outfit] text-xs text-white/30"
            variants={fadeUp}
            custom={13}
          >
            Eğitim satın aldınız ama erişiminiz aktif değil mi?{' '}
            <Link to="/panel/destek" className="text-purple-400/60 hover:text-purple-400 underline transition-colors">
              Destek alın
            </Link>
          </motion.p>
        </motion.div>
      </div>
    </div>
  );
}
